import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ui.module.css';

type Scorer = (password: string, inputs: string[]) => number;

let scorer: Promise<Scorer> | undefined;

/**
 * Loads zxcvbn and its dictionaries on first use: they weigh more than the
 * rest of the application, and most visits never type a password.
 */
function loadScorer(): Promise<Scorer> {
  scorer ??= Promise.all([
    import('@zxcvbn-ts/core'),
    import('@zxcvbn-ts/language-common'),
    import('@zxcvbn-ts/language-en'),
  ]).then(([core, common, en]) => {
    const zxcvbn = new core.ZxcvbnFactory({
      dictionary: { ...common.dictionary, ...en.dictionary },
      graphs: common.adjacencyGraphs,
    });

    return (password, inputs) => zxcvbn.check(password, inputs).score;
  });

  return scorer;
}

export interface PasswordStrengthProps {
  password: string;
  userInputs?: string[];
}

/** A four-step meter with a word describing how hard the password is to guess. */
export function PasswordStrength({
  password,
  userInputs = [],
}: PasswordStrengthProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const [score, setScore] = useState<number | null>(null);
  const inputs = userInputs.join('\u0000');

  useEffect(() => {
    if (password === '') {
      setScore(null);

      return;
    }

    let cancelled = false;
    void loadScorer().then((score) => {
      if (!cancelled) {
        setScore(score(password, inputs.split('\u0000')));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [password, inputs]);

  if (score === null) {
    return null;
  }

  const labels = t('auth.strength', { returnObjects: true }) as string[];

  return (
    <div aria-live="polite">
      <div className={styles.meter} aria-hidden="true">
        {[1, 2, 3, 4].map((step) => (
          <span
            key={step}
            className={[
              styles.meterBar,
              step <= Math.max(score, 1) ? styles[`meterOn${String(score)}`] : '',
            ]
              .filter(Boolean)
              .join(' ')}
          />
        ))}
      </div>
      <span className={styles.hint}>{labels[score]}</span>
    </div>
  );
}
